/**
 * Google Analytics 4 Event Tracking Utilities
 * Reference: https://developers.google.com/analytics/devguides/collection/ga4/reference/events
 */

import { CartItem } from "@/models/cart";

declare global {
  interface Window {
    dataLayer?: any;
  }
}

/**
 * Common GA4 item brand constant
 */
export const GA4_ITEM_BRAND = "Senco";

interface GA4Item {
  item_id: string;
  item_name: string;
  item_brand?: string;
  item_category?: string;
  item_variant?: string;
  price: number;
  quantity: number;
}

interface AddShippingInfoParams {
  currency: string;
  value: number;
  coupon?: string;
  shipping_tier?: string;
  items: GA4Item[];
}

interface ViewItemListParams {
  item_list_id: string;
  item_list_name: string;
  items: GA4Item[];
}

/**
 * Format cart items for GA4 ecommerce tracking
 */
export const formatCartItemsForGA4 = (items: CartItem[]): GA4Item[] => {
  return items.map((item) => ({
    item_id: String(item.id),
    item_name: item.name || item.product_name,
    item_brand: GA4_ITEM_BRAND,
    item_category: item.data?.type
      ? [...item.data.type].reverse()[0]?.name_en
      : undefined,
    item_variant: item.data?.purity || undefined,
    price: item.price || item.product_price,
    quantity: 1,
  }));
};

/**
 * Track add_shipping_info event
 * Triggered when user adds shipping information (address) during checkout or in profile
 *
 * @param params - Event parameters including cart value, items, and shipping info
 */
export const trackAddShippingInfo = (params: AddShippingInfoParams): void => {
  try {
    if (typeof window === "undefined" || !window.dataLayer) {
      return;
    }

    // Clear previous ecommerce data
    window.dataLayer.push({ ecommerce: null });

    // Push add_shipping_info event
    window.dataLayer.push({
      event: "add_shipping_info",
      ecommerce: {
        currency: params.currency || "INR",
        value: params.value,
        coupon: params.coupon || undefined,
        shipping_tier: params.shipping_tier || "Standard",
        items: params.items,
      },
    });

    console.log("[GA4] add_shipping_info event tracked", params);
  } catch (error) {
    console.error("[GA4] Error tracking add_shipping_info:", error);
  }
};

/**
 * Track view_item_list event
 * Triggered when user views a list of items (category page, search results, etc.)
 *
 * @param params - Event parameters including list ID, name, and items
 */
export const trackViewItemList = (params: ViewItemListParams): void => {
  try {
    if (typeof window === "undefined" || !window.dataLayer) {
      return;
    }

    // Clear previous ecommerce data
    window.dataLayer.push({ ecommerce: null });

    // Push view_item_list event
    window.dataLayer.push({
      event: "view_item_list",
      ecommerce: {
        item_list_id: params.item_list_id,
        item_list_name: params.item_list_name,
        items: params.items,
      },
    });

    console.log("[GA4] view_item_list event tracked", params);
  } catch (error) {
    console.error("[GA4] Error tracking view_item_list:", error);
  }
};
